import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { sessionsByProjectGroupSelector } from "../../../stats/state/statsSlice";
import type { DateRange } from "../DateRangePicker";
import { getSessionDate, isSessionInDateRange } from "../../utils/dateUtils";

interface TopParcoursChartProps {
  dateRange: DateRange;
  pageSize?: number;
}

const COLORS = [
  "#00c889",
  "#00b47b",
  "#009667",
  "#007852",
  "#006243",
  "#004d35",
  "#003d2a",
  "#002d1f",
];
const MAX_LABEL_LENGTH = 15;

// Truncate label for Y-axis display
const truncateLabel = (label: string) => {
  if (!label) return "";
  return label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}...` : label;
};

// Custom Y-axis tick: renders the (truncated) label on a single line.
// Recharts' default tick forwards the YAxis `width` to its <Text>, which then
// word-wraps multi-word labels onto a 2nd line that overlaps neighbouring ticks
// when rows are crowded. Rendering a plain <text> (no width) prevents wrapping.
interface YAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}
const ParcoursLabelTick = ({ x, y, payload }: YAxisTickProps) => (
  <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#374151">
    {truncateLabel(payload?.value ?? "")}
  </text>
);

const TopParcoursChart = ({ dateRange, pageSize = 5 }: TopParcoursChartProps) => {
  const { t } = useTranslation();
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);

  // Reset page when dateRange changes
  useEffect(() => {
    setCurrentPage(0);
  }, [dateRange]);

  // Get all data sorted by sessions
  const allData = useMemo(() => {
    const parcoursData: Array<{
      name: string;
      sessions: number;
      id: number;
    }> = [];

    sessionsByProjectGroup.forEach((group) => {
      let sessionCount = 0;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          sessionCount++;
        });
      });

      if (sessionCount > 0) {
        parcoursData.push({
          name: group.project_group_name,
          sessions: sessionCount,
          id: group.project_group_id,
        });
      }
    });

    // Sort by session count
    return parcoursData.sort((a, b) => b.sessions - a.sessions);
  }, [sessionsByProjectGroup, dateRange]);

  // Paginated data
  const chartData = useMemo(() => {
    const start = currentPage * pageSize;
    return allData.slice(start, start + pageSize);
  }, [allData, currentPage, pageSize]);

  const totalPages = Math.ceil(allData.length / pageSize);
  const hasData = allData.length > 0;

  // Navigation handlers
  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  // Ranking info
  const rankingStart = currentPage * pageSize + 1;
  const rankingEnd = Math.min((currentPage + 1) * pageSize, allData.length);
  const rankingText = `${rankingStart}-${rankingEnd} / ${allData.length}`;

  return (
    <div className="h-full flex flex-col">
      {/* Navigation controls */}
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <button
          onClick={goToPrevPage}
          disabled={currentPage === 0}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t("statsNew.charts.prevPage")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <span className="text-sm text-gray-500 font-medium">{rankingText}</span>

        <button
          onClick={goToNextPage}
          disabled={currentPage >= totalPages - 1}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t("statsNew.charts.nextPage")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          {t("statsNew.charts.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%" className="flex-1">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={true}
              vertical={false}
              stroke="#e5e7eb"
            />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={<ParcoursLabelTick />}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              width={130}
              interval={0}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
              cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
              formatter={(value: number) => [value, t("statsNew.charts.sessions")]}
              labelFormatter={(label: string) => label}
            />
            <Bar dataKey="sessions" radius={[0, 4, 4, 0]} maxBarSize={30}>
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TopParcoursChart;
